import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Eye, EyeOff, Shield, Lock, AlertCircle, Loader2 } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { usePanelAuth, formatApiError } from './PanelAuthContext';

export default function OfficialGateway() {
  const nav = useNavigate();
  const { gatewayLogin, gwToken, bootChecked } = usePanelAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (bootChecked && gwToken) return <Navigate to="/official/portal-selection" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await gatewayLogin(email.trim(), password, remember);
      nav('/official/portal-selection', { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900" data-testid="gateway-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-6 text-white">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-emerald-500/20 ring-2 ring-emerald-400/40 mb-3">
            <Shield size={26} className="text-emerald-300" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">ENTORID Official Security Gateway</h1>
          <p className="mt-2 text-sm text-emerald-200/80">Welcome to ENTORID Internal Access Portal</p>
        </div>

        <form
          onSubmit={submit}
          className="bg-white/95 backdrop-blur rounded-2xl shadow-2xl p-6 sm:p-7 border border-white/20"
          data-testid="gateway-form"
        >
          <div className="flex items-center gap-2 mb-5 pb-4 border-b">
            <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-700"><Lock size={16} /></div>
            <div>
              <h2 className="text-base font-bold text-slate-900">Level 1 Authentication</h2>
              <p className="text-xs text-slate-500">Authorized personnel only</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label className="text-sm font-semibold">Official Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@entorid.com"
                className="mt-1 h-11"
                required
                autoComplete="username"
                data-testid="gateway-email"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold">Password</Label>
              <div className="relative mt-1">
                <Input
                  type={show ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-11 pr-10"
                  required
                  autoComplete="current-password"
                  data-testid="gateway-password"
                />
                <button
                  type="button"
                  onClick={() => setShow(s => !s)}
                  className="absolute inset-y-0 right-0 px-3 text-slate-500 hover:text-slate-700"
                  aria-label="Toggle password visibility"
                  data-testid="gateway-toggle-pw"
                >
                  {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between text-xs">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="w-4 h-4" data-testid="gateway-remember" />
                <span>Remember me</span>
              </label>
              <button type="button" onClick={() => alert('Password reset will be available in Phase 3. Contact super-admin to rotate the gateway password from inside the panel.')} className="text-emerald-700 hover:underline">
                Forgot password?
              </button>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm flex items-start gap-2" data-testid="gateway-error">
                <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading || !email || !password}
              className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl"
              data-testid="gateway-submit"
            >
              {loading ? <><Loader2 size={16} className="mr-2 animate-spin" /> Authenticating…</> : 'Continue Securely'}
            </Button>
          </div>

          <p className="mt-5 text-[11px] text-center text-slate-500">
            🔒 Secured with JWT &amp; bcrypt. After 5 failed attempts the account is locked for 30 minutes.
          </p>
        </form>
      </div>
    </div>
  );
}
